import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { ComposableMap, Geographies, Geography, Marker } from 'react-simple-maps';
import { geoCentroid, geoMercator, geoPath as d3GeoPath } from 'd3-geo';
import { scaleLinear } from 'd3-scale';
import axios from 'axios';
import { ArrowLeft, ChevronDown, ChevronUp, Landmark, Scale } from 'lucide-react';
import CandidatoAvatar from './CandidatoAvatar';
import {
  BRAZIL_GEO_URL, STATE_NAMES, STATE_LABEL_OFFSETS, PARTY_COLORS,
  STATE_CODE_MAP, capitalize,
} from '../pages/cleptocracia/constants';
import { useContainerSize } from '../pages/cleptocracia/hooks';
import PoliticoHoverCard from '../pages/cleptocracia/PoliticoHoverCard';

function formatPop(val) {
  if (val >= 1e6) return `${(val / 1e6).toFixed(1)}M`;
  if (val >= 1e3) return `${(val / 1e3).toFixed(0)}mil`;
  return String(val);
}

function PartyBar({ partidos, total }) {
  if (!partidos?.length || !total) return null;
  return (
    <div className="congress-card__bar">
      {partidos.map(({ partido, count, color }) => (
        <div
          key={partido}
          className="congress-card__bar-seg"
          style={{ width: `${(count / total) * 100}%`, background: color || PARTY_COLORS[partido] || '#555' }}
          title={`${partido}: ${count}`}
        />
      ))}
    </div>
  );
}

function CongressCard({ icon: Icon, title, total, ano, partidos, accentColor, active, onClick }) {
  const top = (partidos || []).slice(0, 8).map(p => ({
    ...p,
    color: PARTY_COLORS[p.partido] || '#888',
  }));

  return (
    <div
      className={`congress-card ${active ? 'congress-card--active' : ''}`}
      style={{ borderColor: active ? accentColor : `${accentColor}30`, cursor: 'pointer' }}
      onClick={onClick}
    >
      <div className="congress-card__header">
        <Icon size={16} style={{ color: accentColor }} />
        <div>
          <p className="congress-card__title">{title}</p>
          <p className="congress-card__meta">
            {total} eleitos{ano ? ` · ${ano}` : ''}
            {active && <span className="congress-card__active-hint"> · no mapa</span>}
          </p>
        </div>
      </div>
      <PartyBar partidos={top} total={total} />
      <div className="congress-card__parties">
        {top.map(({ partido, count, color }) => (
          <span key={partido} className="congress-card__party-tag" style={{ borderColor: `${color}40`, background: `${color}18` }}>
            <span className="congress-card__party-dot" style={{ background: color }} />
            <span className="congress-card__party-name">{partido}</span>
            <span className="congress-card__party-count">{count}</span>
          </span>
        ))}
      </div>
    </div>
  );
}

function BrazilMap({ governors, presidente, popMap, popColorScale, onSelectState, senado, camara }) {
  const [hovered, setHovered] = useState(null);
  const [hoveredPres, setHoveredPres] = useState(null);
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });
  const [overlay, setOverlay] = useState(null);
  const containerRef = useRef(null);
  const { width: cw } = useContainerSize(containerRef);

  const svgW = Math.max(400, cw || 600);
  const svgH = Math.round(svgW * 0.93);
  const scale = Math.round(svgW * 1.13);

  const gov = hovered ? governors[hovered] : null;
  const hoveredPop = hovered ? popMap[hovered] : null;

  const senadoByUf = useMemo(() => {
    if (!senado?.senadores) return {};
    const m = {};
    senado.senadores.forEach(s => {
      if (!m[s.uf]) m[s.uf] = [];
      m[s.uf].push(s);
    });
    return m;
  }, [senado]);

  const camaraByUf = useMemo(() => {
    if (!camara?.deputados) return {};
    const m = {};
    camara.deputados.forEach(d => {
      if (!m[d.uf]) m[d.uf] = [];
      m[d.uf].push(d);
    });
    return m;
  }, [camara]);

  const toggleOverlay = (type) => setOverlay(prev => prev === type ? null : type);

  const hoveredOverlayData = useMemo(() => {
    if (!hovered || !overlay) return null;
    if (overlay === 'senado') {
      const list = senadoByUf[hovered];
      return list ? { type: 'senado', list } : null;
    }
    if (overlay === 'camara') {
      const list = camaraByUf[hovered];
      return list ? { type: 'camara', list } : null;
    }
    return null;
  }, [hovered, overlay, senadoByUf, camaraByUf]);

  return (
    <>
      {/* ── National Leaders & Congress Cards ── */}
      <div className="brazil-info-cards">
        {presidente && (
          <div className="congress-card" style={{ borderColor: 'rgba(34,197,94,.2)' }}>
            <div className="congress-card__header">
              <Landmark size={16} style={{ color: '#22c55e' }} />
              <div>
                <p className="congress-card__title">Presidência</p>
                <p className="congress-card__meta">{presidente.presidente?.ano || ''}</p>
              </div>
            </div>
            {presidente.presidente && (
              <Link to={`/politica/candidato/${presidente.presidente.sqCandidato}`} className="congress-card__leader congress-card__leader--link"
                onMouseEnter={() => setHoveredPres('presidente')}
                onMouseLeave={() => setHoveredPres(null)}
                onMouseMove={(e) => setMousePos({ x: e.clientX, y: e.clientY })}>
                <CandidatoAvatar candidato={presidente.presidente} foto={presidente.presidente.foto} nome={presidente.presidente.nome} size={36} className="rounded-lg border border-white/10" />
                <div className="congress-card__leader-info">
                  <p className="congress-card__leader-name">{capitalize(presidente.presidente.nome)}</p>
                  <p className="congress-card__leader-role">{presidente.presidente.partido} · Presidente</p>
                </div>
              </Link>
            )}
            {presidente.vicePresidente && (
              <Link to={`/politica/candidato/${presidente.vicePresidente.sqCandidato}`} className="congress-card__leader congress-card__leader--link"
                onMouseEnter={() => setHoveredPres('vice')}
                onMouseLeave={() => setHoveredPres(null)}
                onMouseMove={(e) => setMousePos({ x: e.clientX, y: e.clientY })}>
                <CandidatoAvatar candidato={presidente.vicePresidente} foto={presidente.vicePresidente.foto} nome={presidente.vicePresidente.nome} size={30} className="rounded-lg border border-white/10" />
                <div className="congress-card__leader-info">
                  <p className="congress-card__leader-name">{capitalize(presidente.vicePresidente.nome)}</p>
                  <p className="congress-card__leader-role">{presidente.vicePresidente.partido} · Vice-Presidente</p>
                </div>
              </Link>
            )}
          </div>
        )}

        {senado && (
          <CongressCard
            icon={Scale}
            title="Senado Federal"
            total={senado.total}
            ano={senado.ano}
            partidos={senado.partidos}
            accentColor="#3b82f6"
            active={overlay === 'senado'}
            onClick={() => toggleOverlay('senado')}
          />
        )}

        {camara && (
          <CongressCard
            icon={Landmark}
            title="Câmara dos Deputados"
            total={camara.total}
            ano={camara.ano}
            partidos={camara.partidos}
            accentColor="#f59e0b"
            active={overlay === 'camara'}
            onClick={() => toggleOverlay('camara')}
          />
        )}
      </div>

      {/* ── Map legend ── */}
      <p className="text-[10px] text-[var(--muted)] mb-2 mt-4">
        {overlay === 'senado'
          ? 'Senadores eleitos por estado — clique no card novamente para ocultar'
          : overlay === 'camara'
            ? 'Deputados federais por estado — clique no card novamente para ocultar'
            : 'Cores indicam o tamanho da população — clique em um estado para ver os municípios'}
      </p>

      {!overlay && popColorScale && (
        <div className="state-map__legend">
          <span className="state-map__legend-label">Menor</span>
          <div className="state-map__legend-bar" />
          <span className="state-map__legend-label">Maior pop.</span>
        </div>
      )}

      {/* ── Brazil SVG Map ── */}
      <div ref={containerRef} className="state-map__container"
        onMouseMove={(e) => setMousePos({ x: e.clientX, y: e.clientY })}>
        {cw > 0 && (
          <ComposableMap
            projection="geoMercator"
            projectionConfig={{ center: [-54, -15], scale }}
            width={svgW} height={svgH}
            style={{ width: '100%', height: 'auto' }}
          >
            <Geographies geography={BRAZIL_GEO_URL}>
              {({ geographies }) =>
                geographies.map((geo) => {
                  const sigla = geo.properties.sigla;
                  const pop = popMap[sigla];
                  const baseFill = pop && popColorScale ? popColorScale(pop) : '#16213e';
                  return (
                    <Geography key={geo.rsmKey} geography={geo}
                      onClick={() => !overlay && onSelectState(sigla)}
                      onMouseEnter={() => setHovered(sigla)}
                      onMouseLeave={() => setHovered(null)}
                      style={{
                        default: { fill: baseFill, stroke: '#0a0e1a', strokeWidth: 0.8, cursor: overlay ? 'default' : 'pointer', transition: 'fill 0.2s' },
                        hover: { fill: overlay ? baseFill : '#ffffff', stroke: overlay ? '#384868' : '#e94560', strokeWidth: 1.5, cursor: overlay ? 'default' : 'pointer' },
                        pressed: { fill: '#c73850', stroke: '#ffffff', strokeWidth: 1.2 },
                      }}
                    />
                  );
                })
              }
            </Geographies>

            {/* Governor badges — only when no overlay */}
            {!overlay && (
              <Geographies geography={BRAZIL_GEO_URL}>
                {({ geographies }) =>
                  geographies.map((geo) => {
                    const sigla = geo.properties.sigla;
                    const stateGov = governors[sigla];
                    if (!stateGov?.partido) return null;
                    const centroid = geoCentroid(geo);
                    const partyColor = PARTY_COLORS[stateGov.partido] || '#888';
                    const off = STATE_LABEL_OFFSETS[sigla] || [0, 0];
                    const isSmall = ['DF', 'SE', 'AL', 'RN'].includes(sigla);
                    const badgeR = isSmall ? svgW * 0.008 : svgW * 0.012;
                    const badgeFont = isSmall ? Math.max(svgW * 0.006, 4) : Math.max(svgW * 0.009, 5);
                    const hasOffset = off[0] !== 0 || off[1] !== 0;
                    return (
                      <React.Fragment key={`badge-${sigla}`}>
                        {hasOffset && (
                          <Marker coordinates={centroid}>
                            <line x1={0} y1={0} x2={off[0] * svgW * 0.06} y2={off[1] * svgW * 0.06}
                              stroke="rgba(255,255,255,.15)" strokeWidth={0.6} strokeDasharray="2,2" />
                          </Marker>
                        )}
                        <Marker coordinates={[centroid[0] + off[0], centroid[1] + off[1]]}>
                          <circle r={badgeR} fill={partyColor} stroke="#0a0e1a" strokeWidth={1.2} opacity={0.92} />
                          <text textAnchor="middle" dominantBaseline="central"
                            className="state-map__party-text"
                            style={{ fontSize: `${badgeFont}px` }}>
                            {stateGov.partido.length > 4 ? stateGov.partido.slice(0, 3) : stateGov.partido}
                          </text>
                        </Marker>
                      </React.Fragment>
                    );
                  })
                }
              </Geographies>
            )}

            {/* Senado overlay — senator names per state */}
            {overlay === 'senado' && (
              <Geographies geography={BRAZIL_GEO_URL}>
                {({ geographies }) =>
                  geographies.map((geo) => {
                    const sigla = geo.properties.sigla;
                    const senators = senadoByUf[sigla];
                    if (!senators?.length) return null;
                    const centroid = geoCentroid(geo);
                    const off = STATE_LABEL_OFFSETS[sigla] || [0, 0];
                    const fontSize = Math.max(svgW * 0.009, 5);
                    return (
                      <Marker key={`sen-${sigla}`} coordinates={[centroid[0] + off[0] * 0.5, centroid[1] + off[1] * 0.5]}>
                        <rect
                          x={-svgW * 0.04} y={-senators.length * (fontSize + 3) / 2 - 3}
                          width={svgW * 0.08} height={senators.length * (fontSize + 3) + 6}
                          rx={3} fill="rgba(10,14,26,.85)" stroke="#3b82f6" strokeWidth={0.6}
                        />
                        {senators.map((s, i) => {
                          const pColor = PARTY_COLORS[s.partido] || '#3b82f6';
                          const yPos = -senators.length * (fontSize + 3) / 2 + i * (fontSize + 3) + fontSize;
                          return (
                            <React.Fragment key={s.sqCandidato}>
                              <circle cx={-svgW * 0.04 + 5} cy={yPos - fontSize * 0.3} r={2} fill={pColor} />
                              <text
                                x={-svgW * 0.04 + 10} y={yPos}
                                className="state-map__party-text"
                                style={{ fontSize: `${fontSize}px`, fill: '#fff', fontWeight: 600 }}
                              >
                                {capitalize(s.nome).length > 12 ? capitalize(s.nome).slice(0, 11) + '…' : capitalize(s.nome)}
                              </text>
                            </React.Fragment>
                          );
                        })}
                      </Marker>
                    );
                  })
                }
              </Geographies>
            )}

            {/* Câmara overlay — deputy count per state */}
            {overlay === 'camara' && (
              <Geographies geography={BRAZIL_GEO_URL}>
                {({ geographies }) =>
                  geographies.map((geo) => {
                    const sigla = geo.properties.sigla;
                    const deps = camaraByUf[sigla];
                    if (!deps?.length) return null;
                    const centroid = geoCentroid(geo);
                    const off = STATE_LABEL_OFFSETS[sigla] || [0, 0];
                    const partyCounts = {};
                    deps.forEach(d => { partyCounts[d.partido] = (partyCounts[d.partido] || 0) + 1; });
                    const topParty = Object.entries(partyCounts).sort((a, b) => b[1] - a[1])[0];
                    const topColor = PARTY_COLORS[topParty?.[0]] || '#f59e0b';
                    const r = Math.max(svgW * 0.012, 8) + Math.min(deps.length * 0.15, 6);
                    const fontSize = Math.max(svgW * 0.009, 5);
                    const labelFont = Math.max(svgW * 0.005, 3.5);
                    return (
                      <Marker key={`dep-${sigla}`} coordinates={[centroid[0] + off[0] * 0.5, centroid[1] + off[1] * 0.5]}>
                        <circle r={r} fill={topColor} stroke="#0a0e1a" strokeWidth={1.2} opacity={0.9} />
                        <text textAnchor="middle" y={-1} dominantBaseline="central"
                          className="state-map__party-text"
                          style={{ fontSize: `${fontSize}px`, fill: '#fff', fontWeight: 700 }}>
                          {deps.length}
                        </text>
                        <text textAnchor="middle" y={fontSize * 0.7} dominantBaseline="central"
                          style={{ fontSize: `${labelFont}px`, fill: 'rgba(255,255,255,.7)', fontWeight: 600 }}>
                          {topParty?.[0] || ''}
                        </text>
                      </Marker>
                    );
                  })
                }
              </Geographies>
            )}
          </ComposableMap>
        )}
      </div>

      {/* Hover info — default (no overlay) */}
      {!overlay && hovered && !gov && (
        <div className="state-map__hover-info">
          <p className="state-map__hover-name">{STATE_NAMES[hovered] || hovered}</p>
          {hoveredPop && (
            <p className="state-map__hover-pop">População: {new Intl.NumberFormat('pt-BR').format(hoveredPop)}</p>
          )}
        </div>
      )}

      {!overlay && gov && (
        <PoliticoHoverCard
          politico={gov}
          label={`Governador${gov.genero === 'FEMININO' ? 'a' : ''} — ${STATE_NAMES[hovered] || hovered}`}
          position={mousePos}
          extraInfo={hoveredPop ? `Pop: ${formatPop(hoveredPop)}` : null}
        />
      )}

      {/* Hover info — overlay (senado/camara) */}
      {overlay && hovered && hoveredOverlayData && (
        <div className="congress-hover" style={{ position: 'fixed', left: mousePos.x + 16, top: mousePos.y - 10, zIndex: 50 }}>
          <p className="congress-hover__title">
            {STATE_NAMES[hovered] || hovered} — {hoveredOverlayData.type === 'senado' ? 'Senadores' : 'Deputados Federais'}
          </p>
          <div className="congress-hover__list">
            {hoveredOverlayData.list.slice(0, 15).map(p => (
              <div key={p.sqCandidato} className="congress-hover__item">
                <span className="congress-hover__dot" style={{ background: PARTY_COLORS[p.partido] || '#888' }} />
                <span className="congress-hover__name">{capitalize(p.nome)}</span>
                <span className="congress-hover__party">{p.partido}</span>
              </div>
            ))}
            {hoveredOverlayData.list.length > 15 && (
              <p className="congress-hover__more">+{hoveredOverlayData.list.length - 15} mais</p>
            )}
          </div>
        </div>
      )}

      {/* Hover info — presidente/vice */}
      {hoveredPres === 'presidente' && presidente?.presidente && (
        <PoliticoHoverCard
          politico={presidente.presidente}
          label="Presidente da República"
          position={mousePos}
        />
      )}
      {hoveredPres === 'vice' && presidente?.vicePresidente && (
        <PoliticoHoverCard
          politico={presidente.vicePresidente}
          label="Vice-Presidente"
          position={mousePos}
        />
      )}
    </>
  );
}

function StateMap({ stateSigla, prefeitos, onBack }) {
  const [geoData, setGeoData] = useState(null);
  const [popMunMap, setPopMunMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [hovered, setHovered] = useState(null);
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });
  const [disabledParties, setDisabledParties] = useState(new Set());
  const [showLegend, setShowLegend] = useState(true);
  const containerRef = useRef(null);
  const { width: cw, height: ch } = useContainerSize(containerRef);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    const stateCode = STATE_CODE_MAP[stateSigla.toUpperCase()];
    if (!stateCode) return;

    Promise.all([
      axios.get(`/geodata/${stateCode}`),
      axios.get(`/populacao/estado/${stateSigla.toUpperCase()}`).catch(() => null),
    ])
      .then(([geoRes, popRes]) => {
        if (!alive) return;
        setGeoData(geoRes.data);
        if (popRes?.data?.success) {
          const pm = {};
          (popRes.data.data.municipios || []).forEach(m => { pm[String(m.codigoIbge)] = m.populacao; });
          setPopMunMap(pm);
        }
      })
      .catch(() => {})
      .finally(() => alive && setLoading(false));

    return () => { alive = false; };
  }, [stateSigla]);

  const popColorScale = useMemo(() => {
    const vals = Object.values(popMunMap);
    if (vals.length === 0) return null;
    const maxPop = Math.max(...vals, 1);
    return scaleLinear()
      .domain([0, maxPop * 0.01, maxPop * 0.05, maxPop * 0.2, maxPop])
      .range(['#1a1a2e', '#16213e', '#0f3460', '#e94560', '#ff6b6b']);
  }, [popMunMap]);

  const containerH = Math.max(400, ch || 400);
  const containerW = Math.max(400, cw || 600);

  const { svgW, svgH, pathGen } = useMemo(() => {
    if (!geoData || containerW < 100) return { svgW: 0, svgH: 0, pathGen: null };
    const pad = 30;
    const proj = geoMercator().fitExtent(
      [[pad, pad], [containerW - pad, containerH - pad]],
      geoData
    );
    return { svgW: containerW, svgH: containerH, pathGen: d3GeoPath().projection(proj) };
  }, [geoData, containerW, containerH]);

  const partyStats = useMemo(() => {
    const counts = {};
    for (const p of Object.values(prefeitos || {})) {
      if (p?.partido) counts[p.partido] = (counts[p.partido] || 0) + 1;
    }
    return Object.entries(counts)
      .map(([partido, count]) => ({ partido, count, color: PARTY_COLORS[partido] || '#888' }))
      .sort((a, b) => b.count - a.count);
  }, [prefeitos]);

  const toggleParty = (partido) => {
    setDisabledParties(prev => {
      const next = new Set(prev);
      if (next.has(partido)) next.delete(partido); else next.add(partido);
      return next;
    });
  };

  const toggleAll = () => {
    setDisabledParties(prev => prev.size === 0
      ? new Set(partyStats.map(p => p.partido))
      : new Set());
  };

  if (loading) {
    return (
      <div className="city-map__loading">
        <div className="city-map__spinner" />
        <p className="city-map__loading-text">Carregando mapa de {STATE_NAMES[stateSigla] || stateSigla}...</p>
      </div>
    );
  }

  const features = geoData?.features || [];
  const hoveredFeat = hovered ? features.find(f => String(f.properties?.id) === hovered) : null;
  const hoveredName = hoveredFeat?.properties?.name;
  const hoveredPop = hovered ? popMunMap[hovered] : null;
  const hoveredPrefeito = hoveredName
    ? prefeitos[hoveredName.toUpperCase()] || prefeitos[hoveredName]
    : null;

  return (
    <>
      <div className="flex items-center gap-2 mb-2">
        <button onClick={onBack} className="city-map__back-btn"><ArrowLeft size={18} /></button>
        <h3 className="text-base font-bold text-[var(--text)]">
          {STATE_NAMES[stateSigla] || stateSigla}
          <span className="text-[var(--muted)] font-normal ml-2 text-sm">{features.length} municípios</span>
        </h3>
      </div>

      <p className="text-[10px] text-[var(--muted)] mb-2">
        Cores indicam o tamanho da população — clique em um município para ver detalhes
      </p>

      {popColorScale && (
        <div className="city-map__legend">
          <span className="city-map__legend-label">Menor</span>
          <div className="city-map__legend-bar" />
          <span className="city-map__legend-label">Maior pop.</span>
        </div>
      )}

      {partyStats.length > 0 && (
        <div className="city-map__party-legend">
          <div className="city-map__party-legend-header">
            <button onClick={() => setShowLegend(!showLegend)} className="city-map__party-legend-toggle">
              Partidos ({partyStats.length})
              <span>{showLegend ? <ChevronUp size={10} /> : <ChevronDown size={10} />}</span>
            </button>
            {showLegend && (
              <button onClick={toggleAll} className="city-map__party-legend-all">
                {disabledParties.size === 0 ? 'Ocultar todos' : 'Mostrar todos'}
              </button>
            )}
          </div>
          {showLegend && (
            <div className="city-map__party-legend-grid">
              {partyStats.map(({ partido, count, color }) => {
                const isActive = !disabledParties.has(partido);
                return (
                  <button key={partido} onClick={() => toggleParty(partido)}
                    className={`city-map__party-chip ${isActive ? 'city-map__party-chip--active' : 'city-map__party-chip--inactive'}`}
                    style={{
                      borderColor: isActive ? color : 'rgba(0,0,0,.1)',
                      background: isActive ? `${color}55` : 'rgba(0,0,0,.04)',
                    }}>
                    <span className="city-map__party-chip-dot" style={{ background: isActive ? color : '#999' }} />
                    <span className="city-map__party-chip-name" style={{ color: isActive ? '#111' : '#999' }}>{partido}</span>
                    <span className="city-map__party-chip-count" style={{ color: isActive ? '#333' : '#999' }}>{count}</span>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      )}

      <div ref={containerRef} className="city-map__container"
        onMouseMove={(e) => setMousePos({ x: e.clientX, y: e.clientY })}>
        {pathGen && svgW > 0 && (
          <svg viewBox={`0 0 ${svgW} ${svgH}`} preserveAspectRatio="xMidYMid meet"
            style={{ width: '100%', height: '100%' }}>
            {features.map((feat, i) => {
              const code = String(feat.properties?.id || '');
              const pop = popMunMap[code];
              const isHov = hovered === code;
              const d = pathGen(feat);
              if (!d) return null;
              const baseFill = pop && popColorScale ? popColorScale(pop) : '#1e2d4a';
              return (
                <Link key={code || i} to={`/municipio/${code}`}>
                  <path d={d}
                    fill={isHov ? '#ffffff' : baseFill}
                    stroke={isHov ? '#e94560' : '#0a0e1a'}
                    strokeWidth={isHov ? 1.5 : 0.5}
                    cursor="pointer"
                    style={{ transition: 'fill 0.15s, stroke 0.15s' }}
                    onMouseEnter={() => setHovered(code)}
                    onMouseLeave={() => setHovered(null)}
                  />
                </Link>
              );
            })}
            {features.map((feat, i) => {
              const name = feat.properties?.name;
              if (!name) return null;
              const pref = prefeitos[name.toUpperCase()] || prefeitos[name];
              if (!pref?.partido) return null;
              if (disabledParties.has(pref.partido)) return null;
              const centroid = pathGen.centroid(feat);
              if (!centroid || isNaN(centroid[0])) return null;
              const partyColor = PARTY_COLORS[pref.partido] || '#888';
              const pop = popMunMap[String(feat.properties?.id || '')];
              const isLarge = pop && pop > 100000;
              const r = isLarge ? 6 : 3.5;
              const fontSize = isLarge ? 5 : 0;
              return (
                <g key={`badge-${i}`} transform={`translate(${centroid[0]},${centroid[1]})`} pointerEvents="none">
                  <circle r={r} fill={partyColor} stroke="#0a0e1a" strokeWidth={0.6} opacity={0.85} />
                  {isLarge && fontSize > 0 && (
                    <text textAnchor="middle" dominantBaseline="central"
                      className="state-map__party-text" style={{ fontSize: `${fontSize}px` }}>
                      {pref.partido.length > 3 ? pref.partido.slice(0, 3) : pref.partido}
                    </text>
                  )}
                </g>
              );
            })}
          </svg>
        )}
      </div>

      {hoveredName && !hoveredPrefeito && (
        <div className="city-map__hover-info">
          <p className="city-map__hover-name">{hoveredName}</p>
          {hoveredPop && (
            <p className="city-map__hover-pop">População: {new Intl.NumberFormat('pt-BR').format(hoveredPop)}</p>
          )}
        </div>
      )}

      {hoveredPrefeito && (
        <PoliticoHoverCard
          politico={hoveredPrefeito}
          label={`Prefeito${hoveredPrefeito.genero === 'FEMININO' ? 'a' : ''} — ${hoveredName}`}
          position={mousePos}
          extraInfo={hoveredPop ? `Pop: ${formatPop(hoveredPop)}` : null}
        />
      )}
    </>
  );
}

export default function PoliticalMaps() {
  const [selectedState, setSelectedState] = useState(null);
  const [governors, setGovernors] = useState({});
  const [presidente, setPresidente] = useState(null);
  const [popMap, setPopMap] = useState({});
  const [prefeitos, setPrefeitos] = useState({});
  const [senado, setSenado] = useState(null);
  const [camara, setCamara] = useState(null);

  useEffect(() => {
    axios.get('/politicos/governadores')
      .then(res => { if (res.data?.success) setGovernors(res.data.data); })
      .catch(() => {});
    axios.get('/politicos/presidente')
      .then(res => { if (res.data?.success) setPresidente(res.data.data); })
      .catch(() => {});
    axios.get('/populacao/estados')
      .then(res => {
        if (res.data?.success) {
          const m = {};
          (res.data.data || []).forEach(e => { if (e.sigla) m[e.sigla] = e.populacao; });
          setPopMap(m);
        }
      })
      .catch(() => {});
    axios.get('/politicos/senado')
      .then(res => { if (res.data?.success) setSenado(res.data.data); })
      .catch(() => {});
    axios.get('/politicos/camara')
      .then(res => { if (res.data?.success) setCamara(res.data.data); })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!selectedState) return;
    setPrefeitos({});
    axios.get(`/politicos/prefeitos/${selectedState}`)
      .then(res => { if (res.data?.success) setPrefeitos(res.data.data); })
      .catch(() => {});
  }, [selectedState]);

  const popColorScale = useMemo(() => {
    const vals = Object.values(popMap);
    if (vals.length === 0) return null;
    const maxPop = Math.max(...vals, 1);
    return scaleLinear()
      .domain([0, maxPop * 0.05, maxPop * 0.2, maxPop * 0.5, maxPop])
      .range(['#1a1a2e', '#16213e', '#0f3460', '#e94560', '#ff6b6b']);
  }, [popMap]);

  return (
    <div className="bg-[var(--card)] border border-[var(--border)] rounded-xl p-5 mb-6 state-map">
      <h2 className="text-lg font-bold text-[var(--text)] mb-3">Mapa Político</h2>

      {!selectedState ? (
        <BrazilMap
          governors={governors}
          presidente={presidente}
          popMap={popMap}
          popColorScale={popColorScale}
          onSelectState={setSelectedState}
          senado={senado}
          camara={camara}
        />
      ) : (
        <StateMap
          stateSigla={selectedState}
          prefeitos={prefeitos}
          onBack={() => setSelectedState(null)}
        />
      )}
    </div>
  );
}
